import {
  Users,
  Building2,
  Globe,
  Briefcase,
  GraduationCap,
  BookOpen,
  MapPin,
  Link2,
  Hash,
  Calendar,
  Code2,
  FileText,
  TrendingUp,
  DollarSign,
} from "lucide-react";

export const PERSON_SCHEMA = [
  {
    name: "Identity",
    icon: Users,
    color: "blue",
    fields: [
      { name: "id", type: "integer", required: true, description: "Unique record identifier" },
      { name: "linkedin_id", type: "integer", description: "LinkedIn numeric profile ID" },
      { name: "first_name", type: "string", description: "Given name" },
      { name: "last_name", type: "string", description: "Family name" },
      { name: "photo", type: "string", description: "Profile photo URL" },
      { name: "date_updated", type: "date-time", required: true, description: "Timestamp of last record update" },
    ],
  },
  {
    name: "Profile",
    icon: FileText,
    color: "purple",
    fields: [
      { name: "headline", type: "string", description: "Professional headline or job title" },
      { name: "description", type: "string", description: "Full bio / profile summary" },
      { name: "industry", type: "string", description: "Primary industry vertical" },
      { name: "is_available", type: "boolean", description: "Open to new opportunities flag" },
    ],
  },
  {
    name: "Location",
    icon: MapPin,
    color: "green",
    fields: [
      { name: "country", type: "string", description: "Country of residence" },
      { name: "area", type: "string", description: "State, province, or region" },
      { name: "city", type: "string", description: "City" },
      { name: "location", type: "string", description: "Normalized full location string" },
      { name: "locations", type: "object[]", description: "All location entries associated with the profile" },
      { name: "linkedin_country", type: "string", description: "Country as listed on LinkedIn" },
      { name: "linkedin_area", type: "string", description: "Area as listed on LinkedIn" },
    ],
  },
  {
    name: "LinkedIn Network",
    icon: Link2,
    color: "sky",
    fields: [
      { name: "linkedin_url", type: "string", description: "Full LinkedIn profile URL" },
      { name: "linkedin_slug", type: "string", description: "LinkedIn vanity URL slug" },
      { name: "number_of_connections", type: "integer", description: "Total LinkedIn connections" },
      { name: "number_of_followers", type: "integer", description: "Total LinkedIn followers" },
    ],
  },
  {
    name: "Skills & Experience",
    icon: Briefcase,
    color: "amber",
    fields: [
      { name: "skills", type: "string[]", description: "List of professional skills" },
      { name: "roles", type: "object[]", description: "Work history — title, company, dates, description (16 sub-fields)" },
      { name: "projects", type: "object[]", description: "Personal and professional projects (10 sub-fields)" },
      { name: "volunteering", type: "object[]", description: "Volunteer roles with org and description (7 sub-fields)" },
      { name: "volunteer_causes", type: "string[]", description: "Cause areas of interest" },
      { name: "organizations", type: "object[]", description: "LinkedIn organizations and associations (7 sub-fields)" },
    ],
  },
  {
    name: "Education & Credentials",
    icon: GraduationCap,
    color: "teal",
    fields: [
      { name: "educations", type: "object[]", description: "Degrees — institution, field, GPA, dates (14 sub-fields)" },
      { name: "certifications", type: "object[]", description: "Professional certs with issuer and expiry (12 sub-fields)" },
      { name: "courses", type: "object[]", description: "Completed courses with provider (5 sub-fields)" },
      { name: "honors", type: "object[]", description: "Awards and recognitions (7 sub-fields)" },
      { name: "test_scores", type: "object[]", description: "Standardized test results (5 sub-fields)" },
    ],
  },
  {
    name: "Research & IP",
    icon: BookOpen,
    color: "rose",
    fields: [
      { name: "patents", type: "object[]", description: "Patent filings — title, co-inventors, status (11 sub-fields)" },
      { name: "publications", type: "object[]", description: "Published works, papers, and books (8 sub-fields)" },
    ],
  },
  {
    name: "Languages",
    icon: Globe,
    color: "indigo",
    fields: [
      { name: "languages", type: "object[]", description: "Languages with proficiency level (2 sub-fields)" },
    ],
  },
];

export const ORG_SCHEMA = [
  {
    name: "Identity",
    icon: Building2,
    color: "blue",
    fields: [
      { name: "id", type: "integer", required: true, description: "Unique record identifier" },
      { name: "name", type: "string", required: true, description: "Organization name" },
      { name: "domain", type: "string", description: "Primary web domain" },
      { name: "website", type: "string", description: "Website URL" },
      { name: "linkedin_id", type: "integer", description: "LinkedIn numeric company ID" },
      { name: "linkedin_url", type: "string", description: "LinkedIn company page URL" },
      { name: "linkedin_slug", type: "string", description: "LinkedIn vanity slug" },
      { name: "date_updated", type: "date-time", required: true, description: "Timestamp of last record update" },
    ],
  },
  {
    name: "Profile",
    icon: FileText,
    color: "purple",
    fields: [
      { name: "description", type: "string", required: true, description: "Company description / about" },
      { name: "logo_url", type: "string", required: true, description: "Company logo URL" },
      { name: "founded_date", type: "date", description: "Founding date" },
      { name: "industry", type: "string", description: "Industry classification" },
      { name: "keywords", type: "string[]", description: "Descriptive keywords and tags" },
    ],
  },
  {
    name: "Size & Workforce",
    icon: Users,
    color: "amber",
    fields: [
      { name: "employees_number", type: "number", description: "Exact employee headcount" },
      { name: "employees_range", type: "string", description: "Employee count range (e.g. 51–200)" },
    ],
  },
  {
    name: "Location",
    icon: MapPin,
    color: "green",
    fields: [
      { name: "country", type: "string", description: "HQ country" },
      { name: "area", type: "string", description: "HQ state or region" },
      { name: "city", type: "string", description: "HQ city" },
      { name: "location", type: "string", description: "Full HQ location string" },
      { name: "locations", type: "object[]", description: "All office locations (2 sub-fields)" },
      { name: "addresses", type: "object[]", description: "Structured address records (7 sub-fields)" },
    ],
  },
  {
    name: "Web Presence",
    icon: TrendingUp,
    color: "sky",
    fields: [
      { name: "domain_rank", type: "integer", description: "Domain authority rank" },
      { name: "domain_traffic", type: "integer", description: "Estimated monthly domain traffic" },
      { name: "domain_tranco_rank", type: "integer", description: "Tranco top-site rank" },
    ],
  },
  {
    name: "Funding",
    icon: DollarSign,
    color: "emerald",
    fields: [
      { name: "funding_rounds", type: "object[]", description: "Investment rounds — type, amount, date (3 sub-fields)" },
    ],
  },
  {
    name: "Technology",
    icon: Code2,
    color: "rose",
    fields: [
      { name: "technologies", type: "string[]", description: "Tech stack and tools detected on the domain" },
    ],
  },
];

export const JOB_SCHEMA = [
  {
    name: "Identity",
    icon: Hash,
    color: "blue",
    fields: [
      { name: "id", type: "integer", required: true, description: "Unique record identifier" },
      { name: "source_id", type: "string", required: true, description: "Source system job ID" },
      { name: "url", type: "string", required: true, description: "Original job posting URL" },
    ],
  },
  {
    name: "Content",
    icon: FileText,
    color: "purple",
    fields: [
      { name: "title", type: "string", required: true, description: "Job title" },
      { name: "description", type: "string", required: true, description: "Full job description" },
      { name: "salary_details", type: "string", description: "Compensation information if available" },
    ],
  },
  {
    name: "Status & Timing",
    icon: Calendar,
    color: "amber",
    fields: [
      { name: "is_remote", type: "boolean", required: true, description: "Whether the role is remote-eligible" },
      { name: "is_active", type: "boolean", required: true, description: "Whether the posting is still active" },
      { name: "date_updated", type: "date", required: true, description: "Date record was last updated" },
      { name: "date_featured", type: "date", required: true, description: "Date the posting was featured" },
    ],
  },
  {
    name: "Location",
    icon: MapPin,
    color: "green",
    fields: [
      { name: "location", type: "string", description: "Raw location string from the posting" },
      { name: "parsed_location", type: "object[]", description: "Structured location (city, country)" },
    ],
  },
  {
    name: "Organization",
    icon: Building2,
    color: "teal",
    fields: [
      { name: "organization_id", type: "integer", required: true, description: "Linked organization ID" },
      { name: "organization_name", type: "string", required: true, description: "Hiring organization name" },
      { name: "organization_domain", type: "string", description: "Organization web domain" },
      { name: "organization_linkedin_url", type: "string", required: true, description: "Organization LinkedIn page URL" },
    ],
  },
];

export const TYPE_COLORS = {
  "integer": "bg-violet-500/15 text-violet-300 border-violet-500/20",
  "number": "bg-violet-500/15 text-violet-300 border-violet-500/20",
  "string": "bg-blue-500/15 text-blue-300 border-blue-500/20",
  "boolean": "bg-emerald-500/15 text-emerald-300 border-emerald-500/20",
  "string[]": "bg-amber-500/15 text-amber-300 border-amber-500/20",
  "object[]": "bg-orange-500/15 text-orange-300 border-orange-500/20",
  "date": "bg-teal-500/15 text-teal-300 border-teal-500/20",
  "date-time": "bg-teal-500/15 text-teal-300 border-teal-500/20",
};

export const CATEGORY_ACCENT = {
  blue: "border-blue-500/30 bg-blue-500/5",
  purple: "border-purple-500/30 bg-purple-500/5",
  green: "border-emerald-500/30 bg-emerald-500/5",
  sky: "border-sky-500/30 bg-sky-500/5",
  amber: "border-amber-500/30 bg-amber-500/5",
  teal: "border-teal-500/30 bg-teal-500/5",
  rose: "border-rose-500/30 bg-rose-500/5",
  indigo: "border-indigo-500/30 bg-indigo-500/5",
  emerald: "border-emerald-500/30 bg-emerald-500/5",
};

export const ICON_COLORS = {
  blue: "text-blue-400",
  purple: "text-purple-400",
  green: "text-emerald-400",
  sky: "text-sky-400",
  amber: "text-amber-400",
  teal: "text-teal-400",
  rose: "text-rose-400",
  indigo: "text-indigo-400",
  emerald: "text-emerald-400",
};
